/**
 * Шилжүүлэг — салбар хооронд гутал зөөх. `/transfers` модуль.
 *
 * Урсгал: DRAFT → (scan-out) → send → IN_TRANSIT → receive →
 * RECEIVED | PARTIAL (дутуу гарвал).
 *
 * ⚠️ Илгээхдээ серийн дугаараар уншуулна — гутал бүр биет нэгж, тоо
 *    ширхэг гэж байхгүй. Луны шалгалт клиент дээр эхлээд хийгдэнэ.
 * ⚠️ Хүлээн авалт ЭНД БИШ — «Хүлээн авалт» (`/receiving`) хуудсанд,
 *    гутал бүрийг уншуулахгүй (2026-10-03).
 * ⚠️ «Нийт үнэ» (2026-10-30) — гутлын ОДООГИЙН зарах үнийн нийлбэр;
 *    шилжүүлэх мөчийн үнэ хадгалагддаггүй. Дэлгэрэнгүйд гутал бүр + Excel.
 */

import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatDateTime, formatMnt, formatQty, formatSerialDisplay, formatSize, isValidSerial, money,
  normalizeSerialInput, TRANSFER_STATUS_MN,
} from '@boots/domain';
import { api, type Envelope, type PagedEnvelope } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { Store, TransferDetail, TransferRow } from '../lib/types';
import {
  Button, Empty, ErrorNote, Field, Input, Modal, OkNote,
  PageActions, Select, Spinner, Tabs, Textarea, TransferStatusBadge,
} from '../components/ui';
import { DataTable, type Column } from '../components/data-table';
import { ExcelButton, saveTableXlsx } from '../components/accounting/shared';

/** `/transfers/:id/discrepancy` — дутуу ирсэн гутлын албан ёсны жагсаалт. */
interface DiscrepancyReport {
  code: string;
  sent: number;
  received: number;
  missingCostMnt: string;
  missing: {
    id: string; serial: string | null; size: number; status: string;
    landedCostMnt: string;
    location: { code: string; name: string } | null;
  }[];
}

type Tab = 'all' | 'DRAFT' | 'IN_TRANSIT' | 'done';

export function TransfersPage() {
  const { can, readOnly } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const list = useQuery({
    queryKey: ['transfers'],
    queryFn: () => api<PagedEnvelope<TransferRow>>('/transfers?limit=100'),
  });

  const canSend = can('WAREHOUSE', 'STORE_MANAGER', 'ADMIN') && !readOnly;

  if (list.isPending) return <Spinner />;
  if (list.error) return <ErrorNote>{(list.error as Error).message}</ErrorNote>;

  const rows = list.data.data;
  const filtered = rows.filter((t) =>
    tab === 'all' ? true
      : tab === 'done' ? t.status === 'RECEIVED' || t.status === 'PARTIAL'
        : t.status === tab,
  );

  const counts = {
    all: rows.length,
    DRAFT: rows.filter((t) => t.status === 'DRAFT').length,
    IN_TRANSIT: rows.filter((t) => t.status === 'IN_TRANSIT').length,
    done: rows.filter((t) => t.status === 'RECEIVED' || t.status === 'PARTIAL').length,
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2.5">
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            { key: 'all', label: 'Бүгд', count: counts.all },
            { key: 'DRAFT', label: 'Бэлтгэж буй', count: counts.DRAFT },
            { key: 'IN_TRANSIT', label: 'Замд', count: counts.IN_TRANSIT },
            { key: 'done', label: 'Дууссан', count: counts.done },
          ]}
        />
        {canSend ? (
          <PageActions>
            <Button onClick={() => setCreating(true)} className="ml-auto">＋ Шинэ шилжүүлэг</Button>
          </PageActions>
        ) : null}
      </div>

      {/*
        640px-ээс доош `DataTable` мөрүүдийг өөрийн хүрээтэй карт болгодог
        тул энэ хүрээ, дэвсгэрийг мобайлд авна — эс тэгвээс хүрээ давхарлана.
      */}
      <section className="overflow-hidden rounded-table sm:border sm:border-hair sm:bg-panel">
        <DataTable
          rows={filtered}
          columns={transferColumns()}
          rowKey={(t) => t.id}
          onRowClick={(t) => setOpenId(t.id)}
          empty={<Empty>Шилжүүлэг алга</Empty>}
        />
      </section>

      {creating ? (
        <CreateTransfer
          onClose={() => setCreating(false)}
          onCreated={async (id) => {
            setCreating(false);
            await qc.invalidateQueries({ queryKey: ['transfers'] });
            setOpenId(id);
          }}
        />
      ) : null}

      {openId ? <TransferDetailModal id={openId} onClose={() => setOpenId(null)} /> : null}
    </div>
  );
}

// ------------------------------------------------------------ Үүсгэх
function CreateTransfer({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [fromStoreId, setFrom] = useState('');
  const [toStoreId, setTo] = useState('');
  const [notes, setNotes] = useState('');

  const stores = useQuery({
    queryKey: ['ref-stores'],
    queryFn: () => api<Envelope<Store[]>>('/ref/stores'),
  });

  const create = useMutation({
    mutationFn: () =>
      api<Envelope<{ id: string }>>('/transfers', {
        method: 'POST',
        body: { fromStoreId, toStoreId, ...(notes.trim() ? { notes: notes.trim() } : {}) },
      }),
    onSuccess: (res) => onCreated(res.data.id),
  });

  const opts = (stores.data?.data ?? []).map((s) => ({ value: s.id, label: `${s.code} · ${s.name}` }));
  const ready = fromStoreId && toStoreId && fromStoreId !== toStoreId;

  return (
    <Modal
      title="Шинэ шилжүүлэг"
      subtitle="Салбар сонгосны дараа гутлыг уншуулна"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Болих</Button>
          <Button onClick={() => create.mutate()} disabled={!ready || create.isPending}>
            {create.isPending ? 'Үүсгэж байна…' : 'Үүсгэх'}
          </Button>
        </>
      }
    >
      {stores.isPending ? <Spinner /> : (
        <>
          <Field label="Хаанаас"><Select value={fromStoreId} onChange={setFrom} options={opts} placeholder="Сонгох…" /></Field>
          <Field label="Хаашаа"><Select value={toStoreId} onChange={setTo} options={opts} placeholder="Сонгох…" /></Field>
          <Field label="Тэмдэглэл"><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
          {fromStoreId && fromStoreId === toStoreId ? (
            <ErrorNote>Ижил салбар руу шилжүүлэх боломжгүй</ErrorNote>
          ) : null}
          {create.error ? <ErrorNote>{(create.error as Error).message}</ErrorNote> : null}
        </>
      )}
    </Modal>
  );
}

// ------------------------------------------------------------ Дэлгэрэнгүй
function TransferDetailModal({ id, onClose }: { id: string; onClose: () => void }) {
  const { can, hasPerm, readOnly } = useAuth();
  const qc = useQueryClient();
  const [raw, setRaw] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['transfer', id],
    queryFn: () => api<Envelope<TransferDetail>>(`/transfers/${id}`),
  });

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['transfer', id] });
    await qc.invalidateQueries({ queryKey: ['transfers'] });
  };

  const scan = useMutation({
    mutationFn: (serial: string) =>
      api(`/transfers/${id}/scan-out`, { method: 'POST', body: { serial } }),
    onSuccess: async () => { setRaw(''); setErr(null); setNote('Уншуулав'); await refresh(); },
    onError: (e: Error) => setErr(e.message),
  });

  const send = useMutation({
    mutationFn: () => api(`/transfers/${id}/send`, { method: 'POST' }),
    onSuccess: async () => { setNote('Илгээгдлээ — замд'); setErr(null); await refresh(); },
    onError: (e: Error) => setErr(e.message),
  });

  function submitScan(e: FormEvent) {
    e.preventDefault();
    const serial = normalizeSerialInput(raw);
    if (!isValidSerial(serial)) {
      setErr('Гутлын код буруу байна');
      return;
    }
    scan.mutate(serial);
  }

  const t = q.data?.data;
  const totalMnt = (t?.items ?? []).reduce((a, ti) => a.plus(ti.item.priceMnt), money(0)).toFixed(2);

  const excel = () =>
    t &&
    saveTableXlsx({
      file: `${t.code}.xlsx`,
      title: `Шилжүүлэг ${t.code}`,
      period: `${t.fromStore.name} → ${t.toStore.name} · ${formatDateTime(t.sentAt ?? t.createdAt)}`,
      headers: ['Баркод', 'Размер', 'Үнэ ₮', 'Гарсан', 'Ирсэн'],
      widths: [16, 9, 15, 9, 9],
      rows: [
        ...t.items.map((ti) => [
          ti.item.serial ?? '—', formatSize(ti.item.size), { money: ti.item.priceMnt },
          ti.scannedOut ? 'Тийм' : '', ti.scannedIn ? 'Тийм' : '',
        ]),
        [`Нийт ${t.items.length} ширхэг`, null, { money: totalMnt }, null, null],
      ],
    });

  // Дутуу ирсэн шилжүүлгийн албан ёсны зөрүүний тайлан
  const discrepancy = useQuery({
    queryKey: ['transfer', id, 'discrepancy'],
    queryFn: () => api<Envelope<DiscrepancyReport>>(`/transfers/${id}/discrepancy`),
    enabled: t?.status === 'PARTIAL',
  });

  const canSend = can('WAREHOUSE', 'STORE_MANAGER', 'ADMIN') && !readOnly;
  const scanning = t?.status === 'DRAFT' && canSend;

  return (
    <Modal
      wide
      title={t ? `Шилжүүлэг ${t.code}` : 'Шилжүүлэг'}
      subtitle={t ? `${t.fromStore.name} → ${t.toStore.name} · ${TRANSFER_STATUS_MN[t.status]}` : undefined}
      onClose={onClose}
      footer={
        t ? (
          <>
            <ExcelButton onClick={excel} disabled={t.items.length === 0} />
            <Button variant="ghost" onClick={onClose}>Хаах</Button>
            {t.status === 'DRAFT' && canSend ? (
              <Button onClick={() => send.mutate()} disabled={t.sentCount === 0 || send.isPending}>
                {send.isPending ? 'Илгээж байна…' : `Илгээх (${formatQty(t.sentCount)})`}
              </Button>
            ) : null}
            {t.status === 'IN_TRANSIT' && hasPerm('transfers.receive') && !readOnly ? (
              <Link
                to="/receiving"
                className="inline-flex min-h-11 items-center rounded-full bg-accent px-4 py-2 text-sm
                  font-medium text-white transition hover:bg-accent-fill-hover sm:min-h-0"
              >
                Хүлээн авалт руу →
              </Link>
            ) : null}
          </>
        ) : null
      }
    >
      {q.isPending ? <Spinner /> : q.error ? (
        <ErrorNote>{(q.error as Error).message}</ErrorNote>
      ) : t ? (
        <>
          {scanning ? (
            <form onSubmit={submitScan} className="flex gap-2">
              <Input
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                placeholder="Илгээх гутлыг уншуулах"
                inputMode="numeric"
                autoFocus
                className="num"
                aria-label="Серийн дугаар"
              />
              <Button type="submit" disabled={scan.isPending} className="shrink-0">Уншуулах</Button>
            </form>
          ) : null}

          {err ? <ErrorNote>{err}</ErrorNote> : null}
          {note && !err ? <OkNote>{note}</OkNote> : null}

          {t.status === 'PARTIAL' && discrepancy.data ? (
            <div className="rounded-card border border-alert/30 bg-alert/5 px-4 py-3">
              <p className="text-sm font-medium text-alert-ink">
                Зөрүү: {formatQty(discrepancy.data.data.missing.length)} гутал ирээгүй ·{' '}
                <span className="num">{formatMnt(discrepancy.data.data.missingCostMnt)}</span>
              </p>
              <ul className="mt-1.5 flex flex-col gap-1 text-[12.5px] text-ink-2">
                {discrepancy.data.data.missing.map((m) => (
                  <li key={m.id} className="flex flex-wrap gap-2">
                    <span className="num">{m.serial ? formatSerialDisplay(m.serial) : '—'}</span>
                    <span>размер {m.size}</span>
                    <span className="text-ink-3">
                      одоо: {m.location?.name ?? '—'} · {m.status}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-[11.5px] text-ink-3">
                Дутуу гутал хаана байгааг шалгаж, шаардлагатай бол актална.
              </p>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-4 text-[12.5px] text-ink-2">
            <span>Илгээсэн: <span className="num">{formatQty(t.sentCount)}</span></span>
            <span>Нийт үнэ: <span className="num font-semibold text-ink">{formatMnt(totalMnt)}</span></span>
            <span>Ирсэн: <span className="num">{formatQty(t.receivedCount)}</span></span>
            {t.sentBy ? <span>Илгээгч: {t.sentBy.fullName}</span> : null}
            {t.receivedBy ? <span>Хүлээн авагч: {t.receivedBy.fullName}</span> : null}
          </div>

          <div className="table-pin max-h-[46dvh] overflow-y-auto rounded-card border border-hair">
            <table className="w-full border-collapse text-[13px]">
              <thead className="sticky top-0 bg-well-2">
                <tr>
                  <Th>Баркод</Th>
                  <Th num>Размер</Th>
                  <Th num>Үнэ</Th>
                  <Th>Гарсан</Th>
                  <Th>Ирсэн</Th>
                </tr>
              </thead>
              <tbody>
                {t.items.map((ti) => (
                  <tr key={ti.id} className="transition hover:bg-row-hover">
                    <Td><span className="num">{ti.item.serial ? formatSerialDisplay(ti.item.serial) : '—'}</span></Td>
                    <Td num>{formatSize(ti.item.size)}</Td>
                    <Td num>{formatMnt(ti.item.priceMnt)}</Td>
                    <Td>{ti.scannedOut ? '✓' : <span className="text-ink-3">—</span>}</Td>
                    <Td>
                      {ti.scannedIn ? '✓' : t.status === 'PARTIAL' || t.status === 'RECEIVED'
                        ? <span className="text-alert-ink">дутуу</span>
                        : <span className="text-ink-3">—</span>}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
            {t.items.length === 0 ? <Empty>Гутал уншуулаагүй байна</Empty> : null}
          </div>
        </>
      ) : null}
    </Modal>
  );
}

function Th({ children, num = false }: { children: React.ReactNode; num?: boolean }) {
  return (
    <th
      {...(num ? { 'data-num': 'true' } : {})}
      className={`border-b border-hair px-3 py-2.5 text-[11.5px] font-medium tracking-wide
        text-ink-2 uppercase whitespace-nowrap ${num ? 'text-right' : 'text-left'}`}
    >
      {children}
    </th>
  );
}

function Td({ children, num = false, muted = false }: {
  children: React.ReactNode; num?: boolean; muted?: boolean;
}) {
  return (
    <td
      {...(num ? { 'data-num': 'true' } : {})}
      className={`border-b border-hair/70 px-3 py-2.5 whitespace-nowrap
        ${num ? 'num text-right' : ''} ${muted ? 'text-ink-2' : ''}`}
    >
      {children}
    </td>
  );
}

/** Шилжүүлгийн жагсаалт — 640px-ээс доош шилжүүлэг тус бүрийн карт. */
function transferColumns(): Column<TransferRow>[] {
  return [
    {
      key: 'code',
      header: 'Дугаар',
      mobile: 'title',
      cell: (t) => <span className="num">{t.code}</span>,
    },
    // Салбарын кирилл нэр — `nowrap` тавихгүй, боогдоно
    { key: 'from', header: 'Хаанаас', cell: (t) => <span className="text-ink-2 text-pretty">{t.fromStore.name}</span> },
    { key: 'to', header: 'Хаашаа', cell: (t) => <span className="text-ink-2 text-pretty">{t.toStore.name}</span> },
    { key: 'sent', header: 'Илгээсэн', align: 'right', mobile: 'value', cell: (t) => formatQty(t.sentCount) },
    {
      key: 'received',
      header: 'Ирсэн',
      align: 'right',
      cell: (t) =>
        t.status === 'PARTIAL' ? (
          <span className="text-alert-ink">{formatQty(t.receivedCount)}</span>
        ) : (
          formatQty(t.receivedCount)
        ),
    },
    {
      key: 'value',
      header: 'Нийт үнэ',
      align: 'right',
      cell: (t) => (t.valueMnt ? formatMnt(t.valueMnt) : '—'),
    },
    {
      key: 'status',
      header: 'Төлөв',
      cell: (t) => <TransferStatusBadge status={t.status} />,
    },
    {
      key: 'at',
      header: 'Огноо',
      align: 'right',
      cell: (t) => <span className="text-ink-2">{formatDateTime(t.sentAt ?? t.createdAt)}</span>,
    },
  ];
}
